import axios from 'axios'

import { API_BASE_URL } from '../../../../config/apiConfig'

export const TWO_FACTOR_METHOD_URL = `${API_BASE_URL}/api/v1/users/admin/auth/2fa/method/`
export const TWO_FACTOR_EMAIL_REQUEST_URL = `${API_BASE_URL}/api/v1/users/admin/auth/2fa/email/request/`
export const TWO_FACTOR_EMAIL_VERIFY_URL = `${API_BASE_URL}/api/v1/users/admin/auth/2fa/email/verify/`
export const TWO_FACTOR_SETUP_URL = `${API_BASE_URL}/api/v1/users/admin/auth/2fa/setup/`
export const TWO_FACTOR_VERIFY_URL = `${API_BASE_URL}/api/v1/users/admin/auth/2fa/verify/`

export interface TwoFactorSetupResponseData {
  qr_code?: string
  qr_code_url?: string
  secret?: string
  secret_key?: string
  otp_secret?: string
  manual_entry_key?: string
  temp_token?: string
  message?: string
}

export interface TwoFactorSetupApiResponse {
  ok: boolean
  message?: string
  data?: TwoFactorSetupResponseData
}

export interface TwoFactorVerifyApiResponse {
  ok: boolean
  message?: string
  data?: any
}

/**
 * Call backend 2FA Setup API using temp_token from login
 */
export async function setup2FA(tempToken: string): Promise<TwoFactorSetupApiResponse> {
  if (!tempToken) {
    throw new Error('Temporary authentication token (temp_token) is required')
  }

  const {data} = await axios.post<TwoFactorSetupApiResponse>(
    TWO_FACTOR_SETUP_URL,
    {temp_token: tempToken},
    {
      headers: {
        'Content-Type': 'application/json',
      },
    }
  )

  return data
}

/**
 * Prepared API Service method to verify 2FA OTP code.
 * Easily connect your backend verification endpoint here.
 */
export async function verify2FA(
  tempToken: string,
  otpCode: string
): Promise<TwoFactorVerifyApiResponse> {
  if (!tempToken || !otpCode) {
    throw new Error('Both temp_token and 6-digit OTP code are required')
  }

  const {data} = await axios.post<TwoFactorVerifyApiResponse>(
    TWO_FACTOR_VERIFY_URL,
    {
      temp_token: tempToken,
      otp: otpCode,
    },
    {
      headers: {
        'Content-Type': 'application/json',
      },
    }
  )

  return data
}

/**
 * Call backend to select 2FA method (EMAIL or AUTHENTICATOR)
 */
export async function select2FAMethod(
  tempToken: string,
  method: 'EMAIL' | 'AUTHENTICATOR'
): Promise<any> {
  if (!tempToken) {
    throw new Error('Temporary authentication token (temp_token) is required')
  }

  const {data} = await axios.post(
    TWO_FACTOR_METHOD_URL,
    {
      temp_token: tempToken,
      method: method,
    },
    {
      headers: {
        'Content-Type': 'application/json',
      },
    }
  )

  return data
}

/**
 * Call backend to request an OTP to be sent via email
 */
export async function requestEmailOTP(tempToken: string): Promise<any> {
  if (!tempToken) {
    throw new Error('Temporary authentication token (temp_token) is required')
  }

  const {data} = await axios.post(
    TWO_FACTOR_EMAIL_REQUEST_URL,
    {
      temp_token: tempToken,
    },
    {
      headers: {
        'Content-Type': 'application/json',
      },
    }
  )

  return data
}

/**
 * Call backend to verify Email OTP code
 */
export async function verifyEmailOTP(
  tempToken: string,
  otpCode: string
): Promise<TwoFactorVerifyApiResponse> {
  if (!tempToken || !otpCode) {
    throw new Error('Both temp_token and 6-digit OTP code are required')
  }

  const {data} = await axios.post<TwoFactorVerifyApiResponse>(
    TWO_FACTOR_EMAIL_VERIFY_URL,
    {
      temp_token: tempToken,
      otp: otpCode,
    },
    {
      headers: {
        'Content-Type': 'application/json',
      },
    }
  )

  return data
}

